/**
 * @module app.cm
 */

import { autoinject, singleton, Disposable, BindingEngine, ICollectionObserverSplice } from 'aurelia-framework';
import { ComponentBase, IForm, Sorting, SortDirection } from 'app/shared/lib/essette-site';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { CarePlanService } from 'app/cm/services/care-plan-service';
import { AssessmentService } from 'app/core/services/assessment-service';
import { IAssessmentsConfig } from 'app/core/models/essette-interfaces';
import { apiCall } from 'app/shared/lib/essette-api';
import { CasePageContext } from 'app/cm/lib/case-page-context';
import { getApiSetting } from 'app/shared/lib/api/api-settings';
import { confirmation } from 'app/shared/elements/ess-confirm';
import { bindable } from 'aurelia-typed-observable-plugin';
import { I18N } from 'aurelia-i18n';
import { IApiActionResults, IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { localSort } from 'app/shared/lib/sorting';
import { getReference } from 'app/shared/models/essette-types';
import { CarePlanBarrierWithLocalState } from 'app/cm/models/local-state-extensions';
import { EssAutocompleteCustomElement } from 'app/shared/elements/forms/ess-autocomplete';
import * as CM from 'app/cm/models/essette-api';
import * as Mcg from 'app/ext/mcg-informed/interfaces';
import { guid } from 'app/shared/lib/guid';
import { pushCopy, spliceCopy } from 'app/shared/lib/utilities';


@autoinject
export class AddBarriers extends ComponentBase implements IForm {

    ////****************** Fields and Properties ************************************////
    public barrierSearchQuery: string | null = null;          // the value of the autocomplete textbox
    @bindable.stringNull
    public caseId: string | null = null
    @bindable.boolean
    public showActions: boolean = false;
    @bindable.object({ changeHandler: 'loadCurrentBarriers' })
    public goal: CM.CarePlanGoal | null = null;
    protected localSorting = new Sorting();

    protected barrierSearchResults = new Array<CM.CarePlanBarrierDefinition>();     // array of search results from API for autocomplete (not currently used)
    protected currentBarriers: CM.CarePlanBarrier[] = [];
    protected selectedBarriers: CM.CarePlanBarrierDefinition[] = [];
    protected barrierLookups: CM.CarePlanBarrierDefinition[] = [];
    protected mcgBarriers: CM.CarePlanBarrier[] = [];
    protected selectedMcgBarriers: CM.CarePlanBarrier[] = [];
    @bindable.string
    protected barrierOption: string = 'selectBarriers';
    protected allowDuplicateBarriers: boolean = false;
    protected insertBarriers: CarePlanBarrierWithLocalState[] = [];
    protected selectedBarriersSubscription: Disposable | null = null;
    protected selectedMcgBarriersSubscription: Disposable | null = null;

    protected cachedSortChangeEvent: SortChangeEvent | null = null;

    protected assessmentConfig: IAssessmentsConfig = {
        serviceRootUrl: 'cm/cases',
        targetType: 'CM.Case'
    };

    protected id: guid = guid();

    ////****************** Element References ***************************************////
    protected $autocomplete!: EssAutocompleteCustomElement;


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(private readonly carePlanService: CarePlanService,
        private readonly assessmentService: AssessmentService,
        private readonly bindingEngine: BindingEngine,
        @singleton(true) private context: CasePageContext,
        private readonly i18n: I18N,
        ...rest: any[]) {
        super(...rest);

    }

    protected bind(): void {
        this.assessmentService.configure(this.assessmentConfig);
        this.allowDuplicateBarriers = !!getApiSetting(this.context.settings, 'carePlan.goals.barriers.$allowDuplicates');
        this.loadBarrierDefinitions();
        this.loadCurrentBarriers();
    }

    protected attached(): void {
        this.observeSelectedBarriers();
        this.observeSelectedMcgBarriers();
    }

    protected detached(): void {
        if(this.selectedBarriersSubscription) this.selectedBarriersSubscription.dispose();
        if(this.selectedMcgBarriersSubscription) this.selectedMcgBarriersSubscription.dispose();
    }


    ////****************** Events ***************************************************////
    protected selectedBarriersChanged(splices: ICollectionObserverSplice<CM.CarePlanBarrierDefinition>[]): void {
        if(this.insertBarriers.length === 0 && this.selectedBarriers.length === 0) return;

        for(var i = 0; i < splices.length; i++) {
            var splice = splices[i];

            var barriersAdded = this.selectedBarriers.slice(splice.index, splice.index + splice.addedCount);
            if(barriersAdded.length > 0) {
                barriersAdded.forEach(barrierDefinition => {
                    const barrier: CarePlanBarrierWithLocalState = {
                        isCustom: false,
                        description: barrierDefinition.description,
                        definition: { id: barrierDefinition.id },
                        goal: { id: this.goal?.id },
                        _state: { isSaved: false }
                    };

                    this.insertBarriers = pushCopy(this.insertBarriers, barrier);
                });
                // if the table is currently sorted, apply that sort to the newly added items
                if(this.cachedSortChangeEvent) this.applySort(this.cachedSortChangeEvent, this.insertBarriers);
            }

            if(splice.removed.length) {
                (splice.removed as CM.CarePlanBarrierDefinition[]).forEach(barrierDefinition => {
                    let i = this.insertBarriers.find(b => (b.definition && b.definition.id) === barrierDefinition.id);
                    if(i) this.insertBarriers = spliceCopy(this.insertBarriers, this.insertBarriers.indexOf(i), 1);
                });
            }
        }
    }

    protected selectedMcgBarriersChanged(splices: ICollectionObserverSplice<CM.CarePlanBarrierDefinition>[]): void {
        if(this.insertBarriers.length === 0 && this.selectedMcgBarriers.length === 0) return;

        for(var i = 0; i < splices.length; i++) {
            var splice = splices[i];

            var barriersAdded = this.selectedMcgBarriers.slice(splice.index, splice.index + splice.addedCount);
            if(barriersAdded.length > 0) {
                barriersAdded.forEach(b => {
                    const barrier: CarePlanBarrierWithLocalState = {
                        isCustom: true,
                        description: b.description,
                        goal: { id: this.goal?.id },
                        externalSystem: { code: 'cci' },
                        externalIdentifier: b.externalIdentifier,
                        _state: { isSaved: false }
                    };

                    this.insertBarriers = pushCopy(this.insertBarriers, barrier);
                });
                // if the table is currently sorted, apply that sort to the newly added items
                if(this.cachedSortChangeEvent) this.applySort(this.cachedSortChangeEvent, this.insertBarriers);
            }

            if(splice.removed.length) {
                (splice.removed as CM.CarePlanBarrier[]).forEach(barrier => {
                    let i = this.insertBarriers.find(b => b.description === barrier.description);
                    if(i) this.insertBarriers = spliceCopy(this.insertBarriers, this.insertBarriers.indexOf(i), 1);
                });
            }
        }
    }


    ////****************** Functions ************************************************////

    protected filterBarriers = (barrier: CM.CarePlanBarrierDefinition): boolean => {
        if(this.selectedBarriers.some(b => barrier.id === b.id) && this.barrierOption === 'searchBarriers') {
            return false;
        }
        if(this.allowDuplicateBarriers) { return true; }
        if(!this.currentBarriers && !barrier) { return false; }
        return !this.currentBarriers.some(b => barrier.id === (b.definition && b.definition.id));
    }

    protected filterMcgBarriers = (barrier: CM.CarePlanBarrier): boolean => {
        if(this.selectedMcgBarriers.some(b => barrier.externalIdentifier === b.externalIdentifier) && this.barrierOption === 'searchBarriers') {
            return false;
        }
        if(this.allowDuplicateBarriers) { return true; }
        if(!this.currentBarriers && !barrier) { return false; }
        return !this.currentBarriers.some(b => barrier.externalIdentifier === b.externalIdentifier);
    }

    protected observeSelectedBarriers(): void {
        // observer only works on a modified array. if the array is replaced, the observer will not function for it any longer unless subscribed again
        if(this.selectedBarriersSubscription) this.selectedBarriersSubscription.dispose();

        this.selectedBarriersSubscription = this.bindingEngine
            .collectionObserver(this.selectedBarriers) // observe the selectedBarriers array, which changes in the multi-select Barrier Definition Select element
            .subscribe(this.selectedBarriersChanged.bind(this)); // pass the splice information (addedCount, index, removed) to selectedBarriersChanged method

        // subscription disposed of in detached()
    }

    protected observeSelectedMcgBarriers(): void {
        // observer only works on a modified array. if the array is replaced, the observer will not function for it any longer unless subscribed again
        if(this.selectedMcgBarriersSubscription) this.selectedMcgBarriersSubscription.dispose();

        this.selectedMcgBarriersSubscription = this.bindingEngine
            .collectionObserver(this.selectedMcgBarriers) // observe the selectedMcgBarriers array, which changes in the multi-select Informed by MCG Barriers Select element
            .subscribe(this.selectedMcgBarriersChanged.bind(this)); // pass the splice information (addedCount, index, removed) to selectedMcgBarriersChanged method

        // subscription disposed of in detached()
    }

    protected async addBarrier(barrier: CM.CarePlanBarrierDefinition): Promise<void> {
        this.selectedBarriers.push(barrier);
        this.barrierSearchQuery = '';
        this.barrierSearchResults.splice(0);
    }

    protected removeSelectedBarrier(barrier: CarePlanBarrierWithLocalState): void {
        if(barrier.externalSystem?.code === 'cci') {
            // Find the barrier in the selectedMcgBarriers array and remove it. This will trigger the selectedMcgBarriersChanged() event, which will update insertBarriers
            const sb = this.selectedMcgBarriers.find(b => b.description === barrier.description);
            if(sb) this.selectedMcgBarriers.splice(this.selectedMcgBarriers.indexOf(sb), 1);
        }
        else {
            // Find the barrier in the selectedBarriers array and remove it. This will trigger the selectedBarriersChanged() event, which will update insertBarriers
            const sb = this.selectedBarriers.find(b => b.id === (barrier.definition && barrier.definition.id));
            if(sb) this.selectedBarriers.splice(this.selectedBarriers.indexOf(sb), 1);
        }
        if(this.selectedBarriers.length === 0) this.$autocomplete?.clear();
    }

    protected isDuplicate(barrier: CM.CarePlanBarrierDefinition) {
        return this.currentBarriers.some(b => barrier.id === (b.definition && b.definition.id));
    }

    /** Handles the grid's sort event */
    protected applySort(event: SortChangeEvent, barriers: CM.CarePlanBarrier[]): void {
        const { property, direction } = event.detail;
        this.cachedSortChangeEvent = event;
        const { localSorting } = this;
        if(direction === null) {
            localSorting.remove(property);
            this.restoreDefaultOrder();
        } else {
            localSorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        localSort(barriers, localSorting);
    }

    /** Restores table's display data to the order of original insertion */
    private restoreDefaultOrder(): void {
        this.insertBarriers = this.selectedBarriers.map(barrier => {
            return {
                isCustom: false,
                description: barrier.description,
                definition: { id: barrier.id },
                goal: { id: this.goal?.id },
                _state: { isSaved: false }
            };
        });
    }

    /** Takes a barrier from the insertBarriers grid and constructs a new copy to be sent to the API */
    protected buildSaveBarrier(barrier: CarePlanBarrierWithLocalState): CarePlanBarrierWithLocalState {
        if(!barrier._state) { barrier._state = {}; }

        let saveBarrier: CarePlanBarrierWithLocalState = {
            goal: { id: this.goal?.id },
            isCustom: barrier.isCustom,
            definition: (!barrier.isCustom && getReference(barrier.definition)) || undefined,
            description: (!barrier.definition && barrier.description) || undefined,
            externalSystem: barrier.externalSystem,
            externalIdentifier: barrier.externalIdentifier
        };

        return saveBarrier;
    }

    ////****************** IForm ****************************************************////

    public hasChanges(): boolean {
        throw new Error('Method not implemented.');
    }

    public submittedChanges?: any;

    public async submit(): Promise<boolean> {
        return await this.saveSelectedBarriers();
    }

    public reset(): void {
        this.selectedBarriers.splice(0);
        this.selectedMcgBarriers.splice(0);
        this.insertBarriers.splice(0);
        this.currentBarriers.splice(0);
        this.barrierSearchQuery = '';
        this.barrierSearchResults.splice(0);
        this.barrierOption = 'selectBarriers';
        if(this.$autocomplete) this.$autocomplete.clear();
    }

    ////****************** Service Calls ********************************************////

    @apiCall
    protected async searchBarriers(value: string): Promise<void> {
        if(!value) {
            if(this.barrierSearchResults) this.barrierSearchResults.splice(0);
            return;
        }

        const fields = ['description'];

        try {
            this.state.beginLoad();
            const response = await this.carePlanService.searchBarrierDefinitions(value, undefined, undefined, fields);
            this.barrierSearchResults = response.results;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadCurrentBarriers(): Promise<void> {
        if(!this.goal?.id) return;

        const options: Partial<IQueryLoadOptions> = {
            all: true
        };

        const includes = ['definition'];
        const fields = ['definition.id', 'description'];

        try {
            this.state.beginLoad();
            const [barriersResponse, mcgBarriers] = await Promise.all([
                this.carePlanService.loadBarriersByGoal(this.goal.id, options, includes, fields),
                this.loadMcgBarriers()
            ]);
            this.currentBarriers = barriersResponse.results;
            if(mcgBarriers !== null) this.mcgBarriers = mcgBarriers;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadBarrierDefinitions(): Promise<void> {
        const options: Partial<IQueryLoadOptions> = {
            all: true
        };

        const fields = ['description'];

        try {
            this.state.beginLoad();
            const response = await this.carePlanService.loadBarrierDefinitions(options, undefined, fields);
            this.barrierLookups = response.results;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    @apiCall
    protected async saveSelectedBarriers(): Promise<boolean> {
        const promises = new Array<Promise<IApiActionResults>>();
        if(!this.goal && !this.goal!.id) { return Promise.resolve(false); }

        const duplicateBarriers: string[] = [];

        const saveBarriers = this.insertBarriers
            .filter(b => {
                // If user clicks save a second time, we skip barriers that might have been saved already.
                return !(b._state && b._state.isSaved);
            })
            .map(barrier => {
                if(this.allowDuplicateBarriers && this.currentBarriers.some(b => barrier.id === (b.definition && b.definition.id))) {
                    duplicateBarriers.push(barrier.description || '');
                }

                return this.buildSaveBarrier(barrier);
            });

        if(duplicateBarriers.length > 0) {
            const message: string = this.i18n.tr('ui:labels.duplicate', { count: duplicateBarriers.length, what: '$t(cm:carePlanBarrier.$)' });

            const result = await confirmation.showCustom(message, duplicateBarriers.join('\n'), confirmation.options.StandardConfirm);
            if(result.dialogResponse === confirmation.options.CANCEL) {
                //canceling means we just stop
                return Promise.resolve(false);
            }
        }

        let isSuccessful = false;
        try {
            this.state.beginSave();

            saveBarriers// Create a promise for each CM.CarePlanBarrier
                .forEach(barrier => {
                    promises.push(this.carePlanService.insertCarePlanBarrier(barrier));
                });
            // Execute all promises and wait until they all finish
            const responses = await Promise.all(promises);
            isSuccessful = responses.every(r => r.isSuccessful);
            this.selectedBarriers.splice(0);
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endSave();
            return isSuccessful;
        }

    }

    @apiCall
    protected async loadMcgBarriers(): Promise<CM.CarePlanBarrier[] | null> {
        if(this.goal?.externalSystem?.code !== 'CCI' || this.goal?.assessmentAttachment?.id == null) return null;

        const includes: string[] = ['externalContent'];
        const assessmentResponse = await this.assessmentService.loadAttachment(this.goal.assessmentAttachment.id, includes);
        const attachment = assessmentResponse?.results[0];
        if(!attachment?.externalContent?.content) return null;

        const content: string = attachment.externalContent.content;
        const assessment: Mcg.Assessment = JSON.parse(content);
        const barriers: CM.CarePlanBarrier[] = [];

        for(const section of assessment.sectionsSet) {
            for(const subSection of section.sectionsSet) {
                for(const barrier of subSection.barriersSet) {
                    if(barrier.identified) barriers.push({ id: barrier.uid, description: barrier.text, externalIdentifier: barrier.uid, $display: barrier.text });
                }
            }
        }

        this.barrierOption = 'mcgBarriers';
        if(barriers.length > 0) return barriers;
        return null;
    }

}
